import type { FC, ReactNode } from "react";
import { useMemo, useRef } from "react";
import type { FormMetadataParamsFormType } from "@searchui-searchforms/shared/schemas";
import { usePageContextQuery } from "@/shared/page-context";
import { createSafeContext } from "@/shared/utils/createSafeContext";
import { SCHEMA_VERSION, UBE_EVENT } from "./constants";
import { createDedupTracker } from "./dedup";
import type { DedupKey } from "./dedup";
import { track as dispatchUbeEvent } from "./tracker";
import type { SearchFormContentType, SearchFormInteractedPayload, SearchFormName, SearchFormType } from "./types";

/**
 * Translates the search-forms-wide routing/metadata contract's `formType` spelling
 * (`FormMetadataParamsFormType`, e.g. `"contentbased"`) into the UBE schema's registered enum
 * spelling (`SearchFormType`, e.g. `"content_based"`). The two contracts are independent and
 * happen to share every value except this one.
 */
const toUbeFormType = (formType: FormMetadataParamsFormType): SearchFormType =>
    formType === "contentbased" ? "content_based" : formType;

type SearchFormInteractionFields = Omit<
    SearchFormInteractedPayload,
    "schemaVersion" | "formName" | "formType" | "contentType" | "dbid"
>;

export interface TrackSearchFormInteractionOptions {
    /** Fire at most once per identity+action for the lifetime of this form mount. */
    dedup?: boolean;
}

type TrackSearchFormInteraction = (
    fields: SearchFormInteractionFields,
    options?: TrackSearchFormInteractionOptions,
) => void;

const [InnerProvider, useFormTrackingContext] = createSafeContext<TrackSearchFormInteraction>("FormTrackingContext");

export interface FormTrackingProviderProps {
    formName: SearchFormName;
    formType: FormMetadataParamsFormType;
    contentType?: SearchFormContentType;
    formId?: string;
    children: ReactNode;
}

const toDedupKey = (fields: SearchFormInteractionFields): DedupKey => ({
    action: fields.action ?? "",
    objectName: fields.objectName,
    relation: fields.relation,
    lifeEvent: fields.lifeEvent,
    fieldName: fields.fieldName,
});

export const FormTrackingProvider: FC<FormTrackingProviderProps> = ({
    formName,
    formType,
    contentType,
    formId,
    children,
}) => {
    const dedupTrackerRef = useRef(createDedupTracker());
    const { data: pageContext } = usePageContextQuery();
    const isEnabled = pageContext.runtimeConfig.isUbeSearchFormInteractedEnabled;

    const track = useMemo<TrackSearchFormInteraction>(() => {
        const envelope: Pick<SearchFormInteractedPayload, "formName" | "formType" | "contentType" | "dbid"> = {
            formName,
            formType: toUbeFormType(formType),
            ...(formType === "contentbased" && contentType !== undefined && { contentType }),
            ...(formType === "contentbased" && formId !== undefined && { dbid: formId }),
        };

        return (fields, options) => {
            if (!isEnabled) {
                return;
            }

            if (options?.dedup === true && !dedupTrackerRef.current.shouldTrack(toDedupKey(fields))) {
                return;
            }

            const payload: SearchFormInteractedPayload = {
                schemaVersion: SCHEMA_VERSION.SEARCH_FORM_INTERACTED,
                ...envelope,
                ...fields,
            };
            dispatchUbeEvent(UBE_EVENT.SEARCH_FORM_INTERACTED, payload);
        };
    }, [formName, formType, contentType, formId, isEnabled]);

    return <InnerProvider value={track}>{children}</InnerProvider>;
};

export { useFormTrackingContext };

export const useTrackSearchFormInteraction = (): TrackSearchFormInteraction => useFormTrackingContext();
